// ═══════════════════════════════════════════════════════════
// Debris Store — Zones, Tickets, Contractors, Trucks
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { useAuthStore } from '../auth/auth-store';
import { FIELDS } from '../api/fields';
import { uploadFileToV2, getV2FileDownloadUrl } from '../lib/uploadFile';
import type { DebrisZone, DebrisLoadTicket, DebrisContractor, DebrisTruck, DebrisDisposalSite } from '../types/debris';
import type { QueryOptions } from '../api/types';

const FT = FIELDS.debrisLoadTickets;
const FZ = FIELDS.debrisZones;
const FC = FIELDS.debrisContractors;
const FTr = FIELDS.debrisTrucks;
const FS = FIELDS.debrisDisposalSites;
const FCr = FIELDS.debrisCrews;
const FDoc = FIELDS.debrisDocuments;

function mapTicket(r: Record<string, unknown>): DebrisLoadTicket {
  return {
    id: r.record_id as number, incidentId: String(r[FT.incidentId] || ''),
    ticketNumber: String(r[FT.ticketNumber] || ''), zoneId: String(r[FT.zoneId] || ''),
    contractorId: String(r[FT.contractorId] || ''), truckId: String(r[FT.truckId] || ''),
    disposalSiteId: String(r[FT.disposalSiteId] || ''),
    debrisType: String(r[FT.debrisType] || ''), measurementMode: String(r[FT.measurementMode] || 'volumetric'),
    calculatedCubicYards: String(r[FT.calculatedCubicYards] || '0'), unitCount: String(r[FT.unitCount] || '0'),
    weight: String(r[FT.weight] || '0'), loadingLoadPct: String(r[FT.loadingLoadPct] || '0'),
    towerLoadPct: String(r[FT.towerLoadPct] || '0'), handLoaded: String(r[FT.handLoaded] || ''),
    pickupStopsJson: String(r[FT.pickupStopsJson] || ''), status: (String(r[FT.status] || 'draft')) as DebrisLoadTicket['status'],
    loadingMonitorName: String(r[FT.loadingMonitorName] || ''),
    loadingMonitorSignature: String(r[FT.loadingMonitorSignature] || ''),
    driverPickupSignature: String(r[FT.driverPickupSignature] || ''),
    towerMonitorName: String(r[FT.towerMonitorName] || ''),
    monitorName: String(r[FT.towerMonitorName] || ''),   // alias
    towerMonitorSignature: String(r[FT.towerMonitorSignature] || ''),
    monitorSignature: String(r[FT.towerMonitorSignature] || ''),   // alias
    driverDeliverySignature: String(r[FT.driverDeliverySignature] || ''),
    driverSignature: String(r[FT.driverDeliverySignature] || ''),  // alias
    verifiedAt: String(r[FT.verifiedAt] || ''), notes: String(r[FT.notes] || ''),
    // Derive loadPercentage from towerLoadPct (authoritative) with fallback to loadingLoadPct
    loadPercentage: String(Number(r[FT.towerLoadPct]) || Number(r[FT.loadingLoadPct]) || ''),
    // Fields without V2 columns — default empty
    Rid: r.record_id as number, truckNumber: '', crewId: '', unitSubcategory: '', diameterRange: '',
    weightUnit: '', scaleTicketNumber: '', pickupLat: '', pickupLng: '', pickupAddress: '',
    pickupTime: '', disposalTime: '', qrCode: '', photoBeforeUrl: '', photoAfterUrl: '', photoLoadUrl: '',
  };
}

function mapZone(r: Record<string, unknown>): DebrisZone {
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId: String(r[FZ.incidentId] || ''),
    zoneName: String(r[FZ.zoneName] || ''), zoneCode: String(r[FZ.zoneCode] || ''),
    boundaryGeojson: String(r[FZ.geometry] || ''),
    centerLat: String(r[FZ.centroidLat] || ''), centerLng: String(r[FZ.centroidLng] || ''),
    estimatedCubicYards: String(r[FZ.estimatedCY] || ''), actualCubicYards: String(r[FZ.actualCY] || ''),
    currentPass: String(r[FZ.currentPass] || ''), debrisTypes: '', assignedContractorId: '', creatorEmail: '',
    passHistory: String(r[FZ.passHistory] || ''), status: (String(r[FZ.status] || 'active')) as DebrisZone['status'],
    notes: String(r[FZ.notes] || ''),
  };
}

function mapContractor(r: Record<string, unknown>): DebrisContractor {
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId: String(r[FC.incidentId] || ''),
    orgId: FC.orgId ? String(r[FC.orgId] || '') : '',
    companyName: String(r[FC.companyName] || ''), contractNumber: String(r[FC.contractNumber] || ''),
    contactName: String(r[FC.contactName] || ''), contactPhone: String(r[FC.contactPhone] || ''),
    contactEmail: String(r[FC.contactEmail] || ''),
    contractAmount: String(r[FC.contractAmount] || ''),
    rateVegetative: '', rateCAndD: '', rateSoilMudSand: '', rateHazardousTree: '',
    rateStumpRemoval: '', rateWhiteGoods: '', rateElectronic: '', rateHhw: '',
    status: (String(r[FC.status] || 'active')) as DebrisContractor['status'],
    notes: String(r[FC.notes] || ''),
  };
}

const TRUCK_META_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;

function decodeTruckNotes(raw: string): { notes: string; meta: Record<string, string> } {
  if (!raw) return { notes: '', meta: {} };
  const m = raw.match(TRUCK_META_RE);
  if (!m) return { notes: raw, meta: {} };
  try {
    const meta = JSON.parse(m[1]);
    const notes = raw.replace(TRUCK_META_RE, '').trim();
    return { notes, meta };
  } catch { return { notes: raw, meta: {} }; }
}

function encodeTruckNotes(userNotes: string, metaFields: Record<string, string>): string {
  const clean = userNotes.replace(TRUCK_META_RE, '').trim();
  const nonEmpty = Object.fromEntries(Object.entries(metaFields).filter(([, v]) => v !== '' && v !== undefined));
  if (Object.keys(nonEmpty).length === 0) return clean;
  const metaStr = JSON.stringify(nonEmpty);
  return clean ? `${clean}\n|||TRMETA|||${metaStr}|||ENDTRMETA|||` : `|||TRMETA|||${metaStr}|||ENDTRMETA|||`;
}

function mapTruck(r: Record<string, unknown>): DebrisTruck {
  const rawNotes = (r[FTr.notes] as string) || '';
  const { notes, meta } = decodeTruckNotes(rawNotes);
  return {
    id: r.record_id as number,
    Rid: r.record_id as number,
    contractorId: String((r[FTr.contractorId] as number) || ''),
    incidentId: '',
    truckNumber: (r[FTr.truckNumber] as string) || '',
    licensePlate: (r[FTr.licensePlate] as string) || '',
    truckType: (r[FTr.truckType] as string) || '',
    lengthFt: '',
    widthFt: '',
    heightFt: '',
    cubicYardCapacity: String(Number(r[FTr.capacityCY]) || 0),
    certifiedDate: (r[FTr.certifiedDate] as string) || '',
    certifiedByName: meta.certifiedByName || '',
    certPhotoUrl: '',
    qrCode: '',
    status: ((r[FTr.certStatus] as string) || 'pending') as DebrisTruck['status'],
    hasTailgate: (r[FTr.hasTailgate] as string) || '',
    certExpiresAt: (r[FTr.certExpiresAt] as string) || '',
    certStatus: (r[FTr.certStatus] as string) || 'pending',
    placardNumber: (r[FTr.placardNumber] as string) || '',
    bedPhotoUrl: meta.bedPhotoUrl || '',
    driverName: (r[FTr.driverName] as string) || '',
    driverPhone: (r[FTr.driverPhone] as string) || '',
    notes,
  };
}

// Normalize legacy V1 siteType codes → V2 short codes
const SITE_TYPE_LEGACY: Record<string, string> = {
  temporary_debris_management_site: 'dms',
  temporary_staging_reduction_site: 'tdsr',
  temporary_site: 'tdsr',
  debris_management_site: 'dms',
};

function normalizeSiteType(raw: string): string {
  return SITE_TYPE_LEGACY[raw] ?? raw;
}

function mapSite(r: Record<string, unknown>): DebrisDisposalSite {
  const id = r.record_id as number;
  return {
    id, Rid: id, incidentId: String(r[FS.incidentId] || ''),
    siteName: String(r[FS.siteName] || ''), siteType: normalizeSiteType(String(r[FS.siteType] || '')),
    address: String(r[FS.address] || ''), latitude: String(r[FS.latitude] || ''),
    longitude: String(r[FS.longitude] || ''),
    capacityCubicYards: String(r[FS.capacityCubicYards] || ''),
    currentVolume: String(r[FS.currentVolume] || '0'),
    acceptedTypes: String(r[FS.acceptedTypes] || '[]'),
    operatingHours: '', // operatingHours has no V2 column
    monitorName: String(r[FS.monitorName] || ''),
    monitorEmail: String(r[FS.monitorEmail] || ''),
    status: (String(r[FS.status] || 'open')) as DebrisDisposalSite['status'],
    notes: String(r[FS.notes] || ''),
  };
}

interface DebrisState {
  // Load Tickets (paginated)
  tickets: DebrisLoadTicket[];
  ticketsTotal: number;
  ticketsLoading: boolean;
  ticketsPage: number;
  fetchTickets: (opts?: Partial<QueryOptions>) => Promise<void>;
  setTicketsPage: (page: number) => void;

  // Zones (small table)
  zones: DebrisZone[];
  zonesLoading: boolean;
  fetchZones: () => Promise<void>;
  loadZones: () => Promise<void>;           // alias for fetchZones
  loadingZones: boolean;                     // alias for zonesLoading

  // Contractors (small table)
  contractors: DebrisContractor[];
  fetchContractors: () => Promise<void>;
  loadContractors: () => Promise<void>;      // alias for fetchContractors
  loadingContractors: boolean;
  addContractor: (data: any) => Promise<number | null>;
  deleteContractor: (id: any) => Promise<void>;

  // Trucks (medium table)
  trucks: DebrisTruck[];
  fetchTrucks: () => Promise<void>;
  loadTrucks: () => Promise<void>;           // alias for fetchTrucks
  loadingTrucks: boolean;
  addTruck: (data: any) => Promise<number | null>;

  // Disposal sites (small table)
  disposalSites: DebrisDisposalSite[];
  fetchDisposalSites: () => Promise<void>;
  loadDisposalSites: () => Promise<void>;    // alias for fetchDisposalSites
  loadingSites: boolean;
  deleteDisposalSite: (id: any) => Promise<void>;

  // Crews
  crews: any[];
  loadingCrews: boolean;
  loadCrews: () => Promise<void>;
  createCrew: (data: any) => Promise<number>;
  updateCrew: (id: any, data: any) => Promise<void>;
  deleteCrew: (id: any) => Promise<void>;

  // Documents
  documents: any[];
  loadingDocs: boolean;
  loadDocuments: () => Promise<void>;
  createDocument: (data: any) => Promise<number | null>;
  updateDocument: (id: any, data: any) => Promise<void>;
  uploadDocumentFile: (id: number, file: File) => Promise<void>;
  getDocumentDownloadUrl: (documentId: string) => Promise<string>;
  deleteDocument: (id: any) => Promise<void>;

  // CRUD
  createTicket: (data: Record<string, unknown>) => Promise<number | null>;
  updateTicket: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteTicket: (id: number) => Promise<void>;
  createZone: (data: Record<string, unknown>) => Promise<number | null>;
  updateZone: (id: number, data: Record<string, unknown>) => Promise<void>;
  createContractor: (data: Record<string, unknown>) => Promise<number | null>;
  updateContractor: (id: number, data: Record<string, unknown>) => Promise<void>;
  createTruck: (data: Record<string, unknown>) => Promise<number | null>;
  updateTruck: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteTruck: (id: number) => Promise<void>;
  createDisposalSite: (data: Record<string, unknown>) => Promise<number | null>;
  updateDisposalSite: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteZone: (id: number) => Promise<void>;
}

function zoneToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.zoneName !== undefined) p[FZ.zoneName] = data.zoneName;
  if (data.zoneCode !== undefined) p[FZ.zoneCode] = data.zoneCode;
  if (data.estimatedCY !== undefined) p[FZ.estimatedCY] = data.estimatedCY;
  if (data.currentPass !== undefined) p[FZ.currentPass] = data.currentPass;
  if (data.status !== undefined) p[FZ.status] = data.status;
  if (data.incidentId !== undefined) p[FZ.incidentId] = data.incidentId;
  if (data.geometry !== undefined) p[FZ.geometry] = data.geometry;
  if (data.notes !== undefined) p[FZ.notes] = data.notes;
  return p;
}

function contractorToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.companyName !== undefined) p[FC.companyName] = data.companyName;
  if (data.contractNumber !== undefined) p[FC.contractNumber] = data.contractNumber;
  if (data.contactName !== undefined) p[FC.contactName] = data.contactName;
  if (data.contactPhone !== undefined) p[FC.contactPhone] = data.contactPhone;
  if (data.contactEmail !== undefined) p[FC.contactEmail] = data.contactEmail;
  if (data.contractAmount !== undefined) p[FC.contractAmount] = data.contractAmount;
  if (data.status !== undefined) p[FC.status] = data.status;
  if (data.incidentId !== undefined) p[FC.incidentId] = data.incidentId;
  if (data.notes !== undefined) p[FC.notes] = data.notes;
  // Phase 4 — FK: only write when field is provisioned
  if (data.orgId !== undefined && FC.orgId) p[FC.orgId] = data.orgId;
  return p;
}

export const useDebrisStore = create<DebrisState>((set, get) => ({
  tickets: [], ticketsTotal: 0, ticketsLoading: false, ticketsPage: 1,
  zones: [], zonesLoading: false,
  contractors: [],
  trucks: [],
  disposalSites: [],

  fetchTickets: async (opts) => {
    set({ ticketsLoading: true });
    try {
      const { ticketsPage } = get();
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: FT.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const existingFilters = opts?.filters || [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
        limit: 50, offset: (ticketsPage - 1) * 50,
        sort: { field: 'created_at', direction: 'desc' }, ...opts,
        filters: [...incFilter, ...existingFilters],
      });
      set({ tickets: res.records.map(mapTicket), ticketsTotal: res.total });
    } catch (e) { console.error('[Debris] fetchTickets', e); }
    set({ ticketsLoading: false });
  },

  setTicketsPage: (page) => { set({ ticketsPage: page }); get().fetchTickets(); },

  fetchZones: async () => {
    set({ zonesLoading: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: FZ.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const res = incFilter.length > 0
        ? await queryRecords<Record<string, unknown>>(TABLES.debrisZones, { limit: 200, filters: incFilter })
        : await fetchRecords<Record<string, unknown>>(TABLES.debrisZones, { limit: 200 });
      set({ zones: res.records.map(mapZone) });
    } catch (e) { console.error('[Debris] fetchZones', e); }
    set({ zonesLoading: false });
  },

  fetchContractors: async () => {
    try {
      // Fetch all contractors (no incident filter) so they're available for
      // crew/driver/truck assignment lookups across all incident contexts
      const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisContractors, { limit: 500 });
      set({ contractors: res.records.map(mapContractor) });
    } catch (e) { console.error('[Debris] fetchContractors', e); }
  },

  fetchTrucks: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisTrucks, { limit: 500 });
      set({ trucks: res.records.map(mapTruck) });
    } catch (e) { console.error('[Debris] fetchTrucks', e); }
  },

  fetchDisposalSites: async () => {
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: FS.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const res = incFilter.length > 0
        ? await queryRecords<Record<string, unknown>>(TABLES.debrisDisposalSites, { filters: incFilter, limit: 500 })
        : await fetchRecords<Record<string, unknown>>(TABLES.debrisDisposalSites, { limit: 500 });
      set({ disposalSites: res.records.map(mapSite) });
    } catch (e) { console.error('[Debris] fetchDisposalSites', e); }
  },

  createTicket: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.debrisLoadTickets, data);
      await get().fetchTickets();
      return (res as any).record_id;
    } catch (e) { console.error('[Debris] createTicket', e); return null; }
  },
  updateTicket: async (id, data) => {
    try {
      await updateRecord(TABLES.debrisLoadTickets, id, data);
      await get().fetchTickets();

      // Phase 4 — Auto-create PA ProjectCost when ticket is verified (best effort)
      const isVerifying = data[FT.status] === 'verified' || data[FT.verifiedAt];
      if (isVerifying && TABLES.paProjectCosts) {
        try {
          const ticket = get().tickets.find(t => t.id === id);
          const paProjectId = (data as any).paProjectId ?? (data[FT.paProjectId] as string) ?? '';
          if (paProjectId) {
            const FC2 = FIELDS.paProjectCosts;
            const cy = ticket ? parseFloat(ticket.calculatedCubicYards || '0') : 0;
            const desc = ticket
              ? `Debris — Ticket #${ticket.ticketNumber || id} (${ticket.debrisType || 'General'})`
              : `Debris load ticket #${id}`;
            const payload: Record<string, unknown> = {
              [FC2.costType]:    'debris',
              [FC2.description]: desc,
              [FC2.totalCost]:   cy,
              [FC2.category]:    'debris_removal',
              [FC2.projectId]:   paProjectId,
            };
            if (FC2.sourceModule)   payload[FC2.sourceModule]   = 'debris';
            if (FC2.sourceRecordId) payload[FC2.sourceRecordId] = String(id);
            await createRecord(TABLES.paProjectCosts, payload);
          }
        } catch { /* best effort */ }
      }
    } catch (e) { console.error('[Debris] updateTicket', e); }
  },
  deleteTicket: async (id) => { try { await deleteRecord(TABLES.debrisLoadTickets, id); await get().fetchTickets(); } catch (e) { console.error('[Debris] deleteTicket', e); } },
  createZone: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.debrisZones, zoneToPayload(data)); await get().fetchZones(); return (r as any).record_id; } catch (e) { console.error('[Debris] createZone', e); return null; } },
  updateZone: async (id, data) => { try { await updateRecord(TABLES.debrisZones, id, zoneToPayload(data)); await get().fetchZones(); } catch (e) { console.error('[Debris] updateZone', e); } },
  createContractor: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.debrisContractors, contractorToPayload(data)); await get().fetchContractors(); return (r as any).record_id; } catch (e) { console.error('[Debris] createContractor', e); return null; } },
  updateContractor: async (id, data) => { try { await updateRecord(TABLES.debrisContractors, id, contractorToPayload(data)); await get().fetchContractors(); } catch (e) { console.error('[Debris] updateContractor', e); } },
  createTruck: async (data) => {
    // bedPhotoUrl and certifiedByName have no V2 columns — encode into notes meta
    const notesWithMeta = encodeTruckNotes(String(data.notes || ''), {
      bedPhotoUrl: String(data.bedPhotoUrl || ''),
      certifiedByName: String(data.certifiedByName || ''),
    });
    const p: Record<string, unknown> = {};
    if (data.truckNumber !== undefined) p[FTr.truckNumber] = data.truckNumber;
    if (data.licensePlate !== undefined) p[FTr.licensePlate] = data.licensePlate;
    if (data.truckType !== undefined) p[FTr.truckType] = data.truckType;
    const cap = Number(data.cubicYardCapacity) || Number((data as any).capacityCY) || 0;
    if (cap) p[FTr.capacityCY] = cap;
    if (data.hasTailgate !== undefined) p[FTr.hasTailgate] = data.hasTailgate === 'true' || data.hasTailgate === true;
    if (data.certStatus !== undefined) p[FTr.certStatus] = data.certStatus;
    if (data.certifiedDate !== undefined) p[FTr.certifiedDate] = data.certifiedDate;
    if (data.certExpiresAt !== undefined) p[FTr.certExpiresAt] = data.certExpiresAt;
    if (data.contractorId !== undefined) p[FTr.contractorId] = data.contractorId;
    if (data.placardNumber !== undefined) p[FTr.placardNumber] = data.placardNumber;
    if (data.driverName !== undefined) p[FTr.driverName] = data.driverName;
    if (data.driverPhone !== undefined) p[FTr.driverPhone] = data.driverPhone;
    p[FTr.notes] = notesWithMeta;
    try { const r = await createRecord<Record<string, unknown>>(TABLES.debrisTrucks, p); await get().fetchTrucks(); return (r as any).record_id; } catch (e) { console.error('[Debris] createTruck', e); return null; }
  },
  updateTruck: async (id, data) => {
    const existing = get().trucks.find(t => t.id === id);
    // bedPhotoUrl and certifiedByName have no V2 columns — encode into notes meta
    const notesWithMeta = encodeTruckNotes(
      data.notes !== undefined ? String(data.notes) : (existing?.notes ?? ''),
      {
        bedPhotoUrl: String(data.bedPhotoUrl !== undefined ? data.bedPhotoUrl : (existing?.bedPhotoUrl ?? '')),
        certifiedByName: String(data.certifiedByName !== undefined ? data.certifiedByName : (existing?.certifiedByName ?? '')),
      }
    );
    const p: Record<string, unknown> = { [FTr.notes]: notesWithMeta };
    if (data.truckNumber !== undefined) p[FTr.truckNumber] = data.truckNumber;
    if (data.licensePlate !== undefined) p[FTr.licensePlate] = data.licensePlate;
    if (data.truckType !== undefined) p[FTr.truckType] = data.truckType;
    const cap = data.cubicYardCapacity !== undefined ? Number(data.cubicYardCapacity) : ((data as any).capacityCY !== undefined ? Number((data as any).capacityCY) : undefined);
    if (cap !== undefined && !isNaN(cap)) p[FTr.capacityCY] = cap;
    if (data.hasTailgate !== undefined) p[FTr.hasTailgate] = data.hasTailgate === 'true' || data.hasTailgate === true;
    if (data.certStatus !== undefined) p[FTr.certStatus] = data.certStatus;
    if (data.certifiedDate !== undefined) p[FTr.certifiedDate] = data.certifiedDate;
    if (data.certExpiresAt !== undefined) p[FTr.certExpiresAt] = data.certExpiresAt;
    if (data.contractorId !== undefined) p[FTr.contractorId] = data.contractorId;
    if (data.placardNumber !== undefined) p[FTr.placardNumber] = data.placardNumber;
    if (data.driverName !== undefined) p[FTr.driverName] = data.driverName;
    if (data.driverPhone !== undefined) p[FTr.driverPhone] = data.driverPhone;
    try { await updateRecord(TABLES.debrisTrucks, id, p); await get().fetchTrucks(); } catch (e) { console.error('[Debris] updateTruck', e); throw e; }
  },
  deleteTruck: async (id) => { try { await deleteRecord(TABLES.debrisTrucks, id); await get().fetchTrucks(); } catch (e) { console.error('[Debris] deleteTruck', e); } },
  createDisposalSite: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.siteName !== undefined) p[FS.siteName] = data.siteName;
    if (data.siteType !== undefined) p[FS.siteType] = data.siteType;
    if (data.address !== undefined) p[FS.address] = data.address;
    if (data.latitude !== undefined) p[FS.latitude] = data.latitude;
    if (data.longitude !== undefined) p[FS.longitude] = data.longitude;
    if (data.capacityCubicYards !== undefined) p[FS.capacityCubicYards] = data.capacityCubicYards;
    if (data.currentVolume !== undefined) p[FS.currentVolume] = data.currentVolume;
    if (data.acceptedTypes !== undefined) p[FS.acceptedTypes] = data.acceptedTypes;
    // operatingHours has no V2 column — skip
    if (data.monitorName !== undefined) p[FS.monitorName] = data.monitorName;
    if (data.monitorEmail !== undefined) p[FS.monitorEmail] = data.monitorEmail;
    if (data.status !== undefined) p[FS.status] = data.status;
    if (data.incidentId !== undefined) p[FS.incidentId] = data.incidentId;
    if (data.notes !== undefined) p[FS.notes] = data.notes;
    try { const r = await createRecord<Record<string, unknown>>(TABLES.debrisDisposalSites, p); await get().fetchDisposalSites(); return (r as any).record_id; } catch (e) { console.error('[Debris] createSite', e); return null; }
  },
  updateDisposalSite: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.siteName !== undefined) p[FS.siteName] = data.siteName;
    if (data.siteType !== undefined) p[FS.siteType] = data.siteType;
    if (data.address !== undefined) p[FS.address] = data.address;
    if (data.latitude !== undefined) p[FS.latitude] = data.latitude;
    if (data.longitude !== undefined) p[FS.longitude] = data.longitude;
    if (data.capacityCubicYards !== undefined) p[FS.capacityCubicYards] = data.capacityCubicYards;
    if (data.currentVolume !== undefined) p[FS.currentVolume] = data.currentVolume;
    if (data.acceptedTypes !== undefined) p[FS.acceptedTypes] = data.acceptedTypes;
    // operatingHours has no V2 column — skip
    if (data.monitorName !== undefined) p[FS.monitorName] = data.monitorName;
    if (data.monitorEmail !== undefined) p[FS.monitorEmail] = data.monitorEmail;
    if (data.status !== undefined) p[FS.status] = data.status;
    if (data.notes !== undefined) p[FS.notes] = data.notes;
    try { await updateRecord(TABLES.debrisDisposalSites, id, p); await get().fetchDisposalSites(); } catch (e) { console.error('[Debris] updateSite', e); }
  },
  deleteZone: async (id) => { try { await deleteRecord(TABLES.debrisZones, id); await get().fetchZones(); } catch (e) { console.error('[Debris] deleteZone', e); } },

  // V1 compat aliases
  loadZones: async () => get().fetchZones(),
  loadContractors: async () => get().fetchContractors(),
  loadTrucks: async () => get().fetchTrucks(),
  loadDisposalSites: async () => get().fetchDisposalSites(),
  loadingZones: false,
  loadingSites: false,
  crews: [] as any[],
  loadingCrews: false,
  loadCrews: async () => {
    set({ loadingCrews: true });
    try {
      const r = await fetchRecords<any>(TABLES.debrisCrews, { limit: 200 });
      set({ crews: r.records.map((row: any) => {
        const foremName = row[FCr.foremanName] || '';
        const foremPhone = row[FCr.foremanPhone] || '';
        // crewType and truckId have no DB columns — stored as JSON meta in notes
        const rawNotes = String(row[FCr.notes] || '');
        const { notes: cleanNotes, meta: crewMeta } = decodeTruckNotes(rawNotes);
        return {
          id: row.record_id, Rid: row.record_id,
          contractorId: String(row[FCr.contractorId] || ''),
          crewName: row[FCr.crewName] || '', foremanName: foremName,
          foremanPhone: foremPhone,
          // Drivers.tsx aliases
          driverName: foremName, driverPhone: foremPhone, driverEmail: '',
          memberCount: Number(row[FCr.memberCount]) || 0,
          assignedZone: row[FCr.assignedZone] || '', assignedZoneId: String(row[FCr.assignedZone] || ''),
          status: (row[FCr.status] || 'active') as any,
          notes: cleanNotes,
          // Fields without DB columns — read from notes meta
          crewType: crewMeta.crewType || '',
          truckId: crewMeta.truckId || '',
          resourceFiles: crewMeta.resourceFiles || '[]',
          incidentId: '',
        };
      }) });
    } catch (e) { console.error('[Debris]', e); }
    set({ loadingCrews: false });
  },
  // createContractor and createTruck are implemented above — these aliases delegate back
  addContractor: async (data: any) => (get() as any).createContractor(data),
  addTruck: async (data: any) => (get() as any).createTruck(data),
  createCrew: async (data: any) => {
    try {
      const p: any = {};
      if (data.contractorId) p[FCr.contractorId] = data.contractorId;
      if (data.crewName) p[FCr.crewName] = data.crewName;
      // Accept both foremanName (store) and driverName (Drivers.tsx)
      const nameVal = data.foremanName || data.driverName || '';
      const phoneVal = data.foremanPhone || data.driverPhone || '';
      if (nameVal) p[FCr.foremanName] = nameVal;
      if (phoneVal) p[FCr.foremanPhone] = phoneVal;
      if (data.memberCount !== undefined) p[FCr.memberCount] = data.memberCount;
      if (data.assignedZone || data.assignedZoneId) p[FCr.assignedZone] = data.assignedZone || data.assignedZoneId;
      if (data.status) p[FCr.status] = data.status;
      // crewType, truckId, resourceFiles have no DB columns — encode in notes meta
      p[FCr.notes] = encodeTruckNotes(String(data.notes || ''), {
        crewType: String(data.crewType || ''),
        truckId: String(data.truckId || ''),
        resourceFiles: String(data.resourceFiles || '[]'),
      });
      const r = await createRecord<any>(TABLES.debrisCrews, p);
      await get().loadCrews();
      return r.record_id as number;
    } catch (e) { console.error('[Debris]', e); return 0; }
  },
  deleteContractor: async (id: any) => { try { await deleteRecord(TABLES.debrisContractors, id); await get().fetchContractors(); } catch (e) { console.error('[Debris]', e); } },
  loadingContractors: false,
  deleteCrew: async (id: any) => {
    try { await deleteRecord(TABLES.debrisCrews, id); await get().loadCrews(); }
    catch (e) { console.error('[Debris]', e); }
  },
  updateCrew: async (id: any, data: any) => {
    try {
      const existing = get().crews.find((c: any) => c.id === id);
      const p: any = {};
      if (data.contractorId !== undefined) p[FCr.contractorId] = data.contractorId;
      if (data.crewName !== undefined) p[FCr.crewName] = data.crewName;
      // Accept both foremanName (store) and driverName (Drivers.tsx)
      const nameVal = data.foremanName ?? data.driverName;
      const phoneVal = data.foremanPhone ?? data.driverPhone;
      if (nameVal !== undefined) p[FCr.foremanName] = nameVal;
      if (phoneVal !== undefined) p[FCr.foremanPhone] = phoneVal;
      if (data.memberCount !== undefined) p[FCr.memberCount] = data.memberCount;
      if (data.assignedZone !== undefined) p[FCr.assignedZone] = data.assignedZone;
      if (data.assignedZoneId !== undefined) p[FCr.assignedZone] = data.assignedZoneId;
      if (data.status !== undefined) p[FCr.status] = data.status;
      // crewType, truckId, resourceFiles have no DB columns — encode in notes meta
      p[FCr.notes] = encodeTruckNotes(
        data.notes !== undefined ? String(data.notes) : (existing?.notes ?? ''),
        {
          crewType: String(data.crewType !== undefined ? data.crewType : (existing?.crewType ?? '')),
          truckId: String(data.truckId !== undefined ? data.truckId : (existing?.truckId ?? '')),
          resourceFiles: String(data.resourceFiles !== undefined ? data.resourceFiles : (existing?.resourceFiles ?? '[]')),
        },
      );
      await updateRecord(TABLES.debrisCrews, id, p);
      await get().loadCrews();
    } catch (e) { console.error('[Debris]', e); throw e; }
  },
  deleteDisposalSite: async (id: any) => { try { await deleteRecord(TABLES.debrisDisposalSites, id); await get().fetchDisposalSites(); } catch (e) { console.error('[Debris]', e); } },
  loadingTrucks: false,
  documents: [] as any[],
  loadingDocs: false,
  loadDocuments: async () => {
    set({ loadingDocs: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: FDoc.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const r = incFilter.length > 0
        ? await queryRecords<any>(TABLES.debrisDocuments, { filters: incFilter, limit: 200 })
        : await fetchRecords<any>(TABLES.debrisDocuments, { limit: 200 });
      set({ documents: r.records.map((row: any) => {
        const rawNotes: string = row[FDoc.notes] || '';
        // Extract documentId from notes meta marker (stored alongside attachmentId)
        const docIdMatch = rawNotes.match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
        const fileId = docIdMatch ? docIdMatch[1] : (row[FDoc.file] || '');
        // Strip meta markers from visible description
        const cleanNotes = rawNotes.replace(/\|\|\|DOCID\|\|\|.*/, '').trim();
        return {
          id: row.record_id, title: row[FDoc.title] || '',
          category: row[FDoc.documentType] || '',
          description: cleanNotes,
          documentType: row[FDoc.documentType] || '', notes: cleanNotes,
          uploadedBy: row[FDoc.uploadedBy] || '', incidentId: row[FDoc.incidentId],
          fileId,   // documentId for download-url endpoint
          fileUrl: '',
          visibleTo: '[]', status: 'active',
        };
      }) });
    } catch (e) { console.error('[Debris]', e); }
    set({ loadingDocs: false });
  },
  createDocument: async (data: any) => {
    try {
      const p: any = {};
      if (data.title) p[FDoc.title] = data.title;
      if (data.documentType) p[FDoc.documentType] = data.documentType;
      if (data.incidentId) p[FDoc.incidentId] = data.incidentId;
      if (data.notes) p[FDoc.notes] = data.notes;
      if (data.uploadedBy) p[FDoc.uploadedBy] = data.uploadedBy;
      const r = await createRecord<any>(TABLES.debrisDocuments, p);
      await get().loadDocuments();
      return r.record_id as number;
    } catch (e) { console.error('[Debris]', e); return null; }
  },
  updateDocument: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.title !== undefined) p[FDoc.title] = data.title;
      if (data.notes !== undefined) p[FDoc.notes] = data.notes;
      await updateRecord(TABLES.debrisDocuments, id, p);
      await get().loadDocuments();
    } catch (e) { console.error('[Debris]', e); }
  },
  uploadDocumentFile: async (id: number, file: File) => {
    try {
      const { attachmentId, documentId } = await uploadFileToV2(file, TABLES.debrisDocuments);
      // Store documentId in notes with meta marker — ATTACHMENT field type has write limitations in V2.
      // documentId (inner doc UUID) is what the download-url endpoint needs.
      // attachmentId is the linking reference but attachmentId is not accepted by download-url.
      const existing = get().documents.find(d => d.id === id);
      const currentNotes = existing?.notes || '';
      const notesWithDocId = `${currentNotes}|||DOCID|||${documentId}|||ATTACHID|||${attachmentId}|||`;
      await updateRecord(TABLES.debrisDocuments, id, {
        [FDoc.file]: attachmentId,
        [FDoc.notes]: notesWithDocId,
      });
      await get().loadDocuments();
    } catch (e) { console.error('[Debris uploadDocumentFile]', e); throw e; }
  },
  getDocumentDownloadUrl: async (documentId: string) => {
    return getV2FileDownloadUrl(documentId);
  },
  deleteDocument: async (id: any) => {
    try { await deleteRecord(TABLES.debrisDocuments, id); await get().loadDocuments(); }
    catch (e) { console.error('[Debris]', e); }
  },
}));
